import * as React from 'react';
import { useQuery } from 'react-query';
import type { Client } from 'contracts/Client';
import { api, applyQueryString } from 'services';
import { Formatter } from 'utils';

import { optionsMapper, multiSelectorOptionsMapper } from '../utils';

type ClientOption = {
  value: string;
  label: string;
};

type HookOptions = {
  /**
   * If true, all client ids that share the same trade name will be combined
   * into a single comma separated value
   */
  combineSameTradeNames?: boolean;
};

export function useClientOptions(options?: HookOptions) {
  const fetchOptions = React.useCallback(async () => {
    const url = applyQueryString('/clients', {
      asList: true,
    });

    const { data } = await api.get<Client[]>(url);
    return data;
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ['fetch-client-options'],
    queryFn: fetchOptions,
    initialData: [],
    refetchOnWindowFocus: false,
  });

  const singleSelectOptions = React.useMemo(() => {
    if (!data) return [];
    return getClientOptions(data, options?.combineSameTradeNames).map(
      optionsMapper,
    );
  }, [options?.combineSameTradeNames, data]);

  const multiSelectOptions = React.useMemo(() => {
    if (!data) return [];
    return getClientOptions(data, options?.combineSameTradeNames).map(
      multiSelectorOptionsMapper,
    );
  }, [options?.combineSameTradeNames, data]);

  return {
    singleSelectOptions,
    multiSelectOptions,
    loading: isLoading,
  };
}

const getClientOptions = (
  clients: Client[],
  mergeSameTradeNames = false,
): ClientOption[] => {
  if (!mergeSameTradeNames) {
    return clients.map((client) => {
      const { id, tradeName, document, documentType } = client;

      return {
        label: [tradeName, Formatter.document(document, documentType)].join(' - '),
        value: id.toString(),
      };
    });
  }

  const clientsMap = new Map<string, number[]>();

  for (const client of clients) {
    const { tradeName, id } = client;
    if (clientsMap.has(tradeName)) {
      clientsMap.set(tradeName, [...clientsMap.get(tradeName)!, id]);
    } else {
      clientsMap.set(tradeName, [id]);
    }
  }

  return Array.from(clientsMap.entries()).map((entry) => {
    const [tradeName, ids] = entry;

    return {
      label: tradeName,
      value: ids.toString(),
    };
  }).sort((a, b) => a.label.localeCompare(b.label));
};
